// core/stores/videoCommentsStore.ts — Zustand store for video comments.
//
// Pure client-only, in-memory (owner decision — videos have no comment backing
// on the server). Hydrated once from the typed seed; NEVER persisted to
// localStorage — a refresh intentionally loses working comment text. Mirrors
// commentStore.ts's shape (flat array + setComments/addComment/patchComment)
// but is a fully separate store: video comments must not couple to the
// post-comment store or service.
//
// Flatten rule: parentId always points to a top-level comment id. The store
// enforces no business rules — that is useVideoComments's responsibility.

import { create } from 'zustand';
import type { VideoComment } from '@/core/models/videoComment';
import { seedVideoComments } from '@/core/data/videoComments';

interface VideoCommentState {
  comments: VideoComment[];

  // Replace the entire comment list (used by tests to reset state between runs).
  setComments: (comments: VideoComment[]) => void;

  // Prepend a new comment to the list.
  addComment: (comment: VideoComment) => void;

  // Apply a patch to a comment by id; no-op if not found.
  patchComment: (id: string, patch: Partial<VideoComment>) => void;
}

function freshSeed(): VideoComment[] {
  // Deep-clone the seed so store resets (and test resets) don't mutate the imported const.
  return seedVideoComments.map((c) => ({ ...c }));
}

export const useVideoCommentsStore = create<VideoCommentState>()((set) => ({
  comments: freshSeed(),

  setComments: (comments) => set({ comments }),

  addComment: (comment) => set((s) => ({ comments: [comment, ...s.comments] })),

  patchComment: (id, patch) =>
    set((s) => ({
      comments: s.comments.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    })),
}));

/** Reset the store back to its seed state (test helper — mirrors commentsService._resetForTest). */
export function _resetVideoCommentsForTest(): void {
  useVideoCommentsStore.getState().setComments(freshSeed());
}
